"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface ContourProps {
  /** "rings" = concentric topographic peak; "lines" = horizontal contour bands. */
  variant?: "rings" | "lines";
  /** Tailwind text-colour + opacity class controls the stroke (uses currentColor). */
  className?: string;
  /**
   * Animate the lines "drawing on" when they scroll into view. Respects
   * prefers-reduced-motion (shows instantly). Set false for a static motif.
   */
  animate?: boolean;
  /**
   * Faintness of the motif, applied as an inline style so it can't be broken
   * by a stale/missing Tailwind opacity utility. Colour still comes from the
   * `text-*` class (currentColor). Defaults to a subtle texture.
   */
  opacity?: number;
}

const RINGS = Array.from({ length: 9 }, (_, i) => ({
  cx: 232 + i * 7,
  cy: 252 - i * 5,
  rx: 46 + i * 25,
  ry: 34 + i * 20,
}));

const LINES = Array.from({ length: 10 }, (_, i) => {
  const y = 24 + i * 48;
  return `M0 ${y} C 96 ${y - 20}, 192 ${y + 20}, 288 ${y} S 432 ${y - 20}, 480 ${y}`;
});

/**
 * Decorative topographic contour motif — the Coaley Peak signature element.
 * Replaces blurred blobs / decorative gradients. The stroke inherits
 * `currentColor`, so set colour + opacity via `className` (e.g.
 * `text-secondary/20`). When `animate` is on, the lines sketch themselves in as
 * they enter the viewport. Always aria-hidden.
 */
export function Contour({
  variant = "rings",
  className,
  animate = true,
  opacity = 0.1,
}: ContourProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [drawn, setDrawn] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [lengths, setLengths] = useState<number[]>([]);

  // Measure each line's geometric length so the dash animation is exact.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const shapes = Array.from(
      el.querySelectorAll<SVGGeometryElement>("[data-contour-line]"),
    );
    setLengths(
      shapes.map((s) => {
        try {
          return s.getTotalLength();
        } catch {
          return 2000;
        }
      }),
    );
  }, [variant]);

  // Trigger the draw when scrolled into view (immediately for the hero, and
  // instantly when the user prefers reduced motion or animation is disabled).
  useEffect(() => {
    if (!animate) {
      setDrawn(true);
      return;
    }
    if (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      setReduced(true);
      setDrawn(true);
      return;
    }
    const el = svgRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setDrawn(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.2, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [animate]);

  const lineStyle = (i: number): React.CSSProperties | undefined => {
    if (!animate || reduced) return undefined;
    const len = lengths[i] ?? 2000;
    return {
      strokeDasharray: len,
      strokeDashoffset: drawn ? 0 : len,
      transition: "stroke-dashoffset 1600ms cubic-bezier(0.33, 0, 0.2, 1)",
      transitionDelay: `${i * 110}ms`,
    };
  };

  // Rings are a corner accent: scaled to cover (slice), kept crisp
  // (non-scaling-stroke) and faded away from content so they never cross text.
  // The "lines" bands instead fill wide dark sections edge-to-edge — stretched
  // to the box (none) so all bands spread evenly with no sparse crop/gap, and
  // unmasked since they're a faint white texture on dark that doesn't hurt text.
  const isRings = variant === "rings";
  const maskImage = isRings
    ? "linear-gradient(to bottom left, #000 16%, transparent 64%)"
    : undefined;

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 480 480"
      fill="none"
      aria-hidden="true"
      focusable="false"
      preserveAspectRatio={isRings ? "xMidYMid slice" : "none"}
      style={{ opacity, maskImage, WebkitMaskImage: maskImage }}
      className={cn("pointer-events-none select-none", className)}
    >
      <g
        stroke="currentColor"
        strokeWidth={1}
        vectorEffect={isRings ? "non-scaling-stroke" : undefined}
      >
        {variant === "rings"
          ? RINGS.map((r, i) => (
              <ellipse
                key={i}
                data-contour-line=""
                cx={r.cx}
                cy={r.cy}
                rx={r.rx}
                ry={r.ry}
                transform="rotate(-19 240 248)"
                style={lineStyle(i)}
              />
            ))
          : LINES.map((d, i) => (
              <path key={i} data-contour-line="" d={d} style={lineStyle(i)} />
            ))}
      </g>
    </svg>
  );
}
